import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { 
  useEnhancedCreditPackages,
  useCombinedDeviceAssignment,
  type EnhancedCreditPackage 
} from "@/hooks/use-enhanced-credit-packages";
import { useUserDevices } from "@/hooks/use-user-devices";
import { 
  Package, 
  Monitor, 
  User, 
  CheckCircle, 
  AlertTriangle,
  Search,
  Users,
  Plus,
  Minus,
  Lock,
  Unlock,
  Image
} from "lucide-react";
import { format } from "date-fns";

interface DeviceAllocation {
  deviceId: string;
  deviceName?: string;
  allocatedCredits: number;
  isLocked?: boolean;
}

interface UserCreditPackageManagerProps {
  packages?: EnhancedCreditPackage[];
}

export const UserCreditPackageManager = ({ packages = [] }: UserCreditPackageManagerProps) => {
  const [selectedPackage, setSelectedPackage] = useState<EnhancedCreditPackage | null>(null);
  const [allocations, setAllocations] = useState<DeviceAllocation[]>([]);
  const [deviceSearchTerm, setDeviceSearchTerm] = useState<string>("");
  const [showAllocationForm, setShowAllocationForm] = useState(false);

  // Filter to user-owned packages only
  const userOwnedOnly = packages.filter(pkg => pkg.assignment_type === 'user_owned' && !pkg.is_assigned);
  
  // Refetch function for compatibility
  const refetchPackages = () => {
    // This will be handled by the parent component's query refetch
  };

  const combinedAssignment = useCombinedDeviceAssignment();

  // Get user's assigned devices only
  const { data: userDevices = [] } = useUserDevices();

  // Filter devices based on search term
  const filteredDevices = userDevices.filter(device => {
    if (!deviceSearchTerm) return true;
    return (
      device.device_id.toLowerCase().includes(deviceSearchTerm.toLowerCase()) ||
      device.device_name?.toLowerCase().includes(deviceSearchTerm.toLowerCase())
    );
  });

  const getStatusBadge = (status: string) => {
    return <Badge className="bg-blue-100 text-blue-800">Assigned to You</Badge>;
  };

  const handleDeviceChange = (index: number, deviceId: string) => {
    const device = userDevices?.find(d => d.device_id === deviceId);
    const newAllocations = [...allocations];
    newAllocations[index] = {
      ...newAllocations[index],
      deviceId,
      deviceName: device?.device_name || deviceId
    };
    setAllocations(newAllocations);
  };

  const handleCreditsChange = (index: number, newCredits: number) => {
    if (newCredits < 0) return;
    
    const oldCredits = allocations[index].allocatedCredits;
    const creditDifference = newCredits - oldCredits;
    
    const newAllocations = [...allocations];
    newAllocations[index] = {
      ...newAllocations[index],
      allocatedCredits: newCredits
    };

    // Auto-balance if we're exceeding total and there are unlocked devices to adjust
    const currentTotal = newAllocations.reduce((sum, a) => sum + a.allocatedCredits, 0);
    const totalPackageCredits = selectedPackage?.remaining_credits || 0;
    
    if (currentTotal > totalPackageCredits && creditDifference > 0) {
      const excess = currentTotal - totalPackageCredits;
      const unlockedDevices = newAllocations
        .map((allocation, idx) => ({ allocation, index: idx }))
        .filter(({ allocation, index: idx }) => !allocation.isLocked && idx !== index && allocation.allocatedCredits > 0);
      
      if (unlockedDevices.length > 0) {
        let remainingExcess = excess;
        
        for (const { allocation, index: deviceIndex } of unlockedDevices) {
          const proportion = allocation.allocatedCredits / unlockedDevices.reduce((sum, { allocation: a }) => sum + a.allocatedCredits, 0);
          const reduction = Math.min(
            Math.floor(remainingExcess * proportion),
            allocation.allocatedCredits
          );
          
          newAllocations[deviceIndex].allocatedCredits -= reduction;
          remainingExcess -= reduction;
          
          if (remainingExcess <= 0) break;
        }
        
        while (remainingExcess > 0) {
          const deviceToReduce = unlockedDevices.find(({ allocation }) => allocation.allocatedCredits > 0);
          if (!deviceToReduce) break;
          
          const reductionIndex = newAllocations.findIndex((_, idx) => idx === deviceToReduce.index);
          newAllocations[reductionIndex].allocatedCredits -= 1;
          remainingExcess -= 1;
        }
      } else {
        const maxPossibleCredits = totalPackageCredits - newAllocations
          .filter((_, idx) => idx !== index)
          .reduce((sum, a) => sum + a.allocatedCredits, 0);
        newAllocations[index].allocatedCredits = Math.max(0, maxPossibleCredits);
      }
    }
    
    setAllocations(newAllocations);
  };

  const toggleLock = (index: number) => {
    const newAllocations = [...allocations];
    newAllocations[index] = {
      ...newAllocations[index],
      isLocked: !newAllocations[index].isLocked
    };
    setAllocations(newAllocations);
  };

  const addDeviceSlot = () => {
    setAllocations([...allocations, { deviceId: '', deviceName: '', allocatedCredits: 0, isLocked: false }]);
  };

  const removeDeviceSlot = (index: number) => {
    if (allocations.length <= 1) return;
    
    const removedCredits = allocations[index].allocatedCredits;
    const newAllocations = allocations.filter((_, i) => i !== index);
    
    if (removedCredits > 0 && newAllocations.length > 0) {
      const creditsPerDevice = Math.floor(removedCredits / newAllocations.length);
      const remainder = removedCredits % newAllocations.length;
      
      newAllocations.forEach((allocation, idx) => {
        allocation.allocatedCredits += creditsPerDevice + (idx < remainder ? 1 : 0);
      });
    }
    
    setAllocations(newAllocations);
  };

  const redistributeCredits = () => {
    if (allocations.length === 0 || !selectedPackage) return;
    
    const totalCredits = selectedPackage.remaining_credits;
    const creditsPerDevice = Math.floor(totalCredits / allocations.length);
    const remainder = totalCredits % allocations.length;
    
    const newAllocations = allocations.map((allocation, index) => ({
      ...allocation,
      allocatedCredits: creditsPerDevice + (index < remainder ? 1 : 0),
      isLocked: false
    }));
    
    setAllocations(newAllocations);
  };

  const handleAllocateCredits = async () => {
    if (!selectedPackage || allocations.length === 0) return;

    // Validate all devices are selected
    const hasEmptyDevices = allocations.some(a => !a.deviceId);
    if (hasEmptyDevices) {
      return;
    }

    // Validate total credits match
    const totalAllocated = allocations.reduce((sum, a) => sum + a.allocatedCredits, 0);
    if (totalAllocated !== selectedPackage.remaining_credits) {
      return;
    }

    try {
      // Allocate to each device
      for (const allocation of allocations) {
        await combinedAssignment.mutateAsync({
          deviceId: allocation.deviceId,
          userId: selectedPackage.purchaser_user_id!,
          creditPackageId: selectedPackage.id,
          assignmentType: 'user_owned',
          validatePurchaser: false
        });
      }

      // Call BuildShip API for device assignment
      try {
        const buildshipAuth = await supabase.functions.invoke('get-buildship-token');
        
        if (buildshipAuth.data?.token) {
          for (const allocation of allocations) {
            await fetch('https://i56o7w.buildship.run/device-assignment', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${buildshipAuth.data.token}`,
              },
              body: JSON.stringify({
                orderId: selectedPackage.id, // Use credit package ID as orderId for user-owned credits
                deviceId: allocation.deviceId,
                deviceName: allocation.deviceName || '',
                allocatedCredits: allocation.allocatedCredits,
                customerUserId: selectedPackage.purchaser_user_id,
                organizationId: selectedPackage.organization_id,
                assignedUserCount: 1 // User-owned credits are assigned to 1 user
              }),
            });
          }
        }
      } catch (error) {
        console.warn("⚠️ BuildShip API call failed:", error);
        // Don't fail the entire operation if BuildShip API fails
      }

      setShowAllocationForm(false);
      setSelectedPackage(null);
      setAllocations([]);
      refetchPackages();
    } catch (error) {
      console.error('Error allocating credits:', error);
    }
  };

  const startAllocation = (pkg: EnhancedCreditPackage) => {
    setSelectedPackage(pkg);
    setShowAllocationForm(true);
    setAllocations([{ deviceId: '', deviceName: '', allocatedCredits: pkg.remaining_credits, isLocked: false }]);
    setDeviceSearchTerm("");
  };

  const totalAllocated = allocations.reduce((sum, a) => sum + a.allocatedCredits, 0);
  const remainingCredits = (selectedPackage?.remaining_credits || 0) - totalAllocated;
  const isValid = selectedPackage && totalAllocated === selectedPackage.remaining_credits && allocations.every(a => a.deviceId);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Users className="h-5 w-5" />
          Available Credit Packages
        </h3>
        <Badge variant="outline">
          {userOwnedOnly.length} packages awaiting allocation
        </Badge>
      </div>

      {userOwnedOnly.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No user-owned credit packages found.</p>
            <p className="text-sm text-muted-foreground mt-2">
              User-owned packages are created when orders are completed without specific device assignments.
            </p>
          </CardContent>
        </Card>
      ) : showAllocationForm && selectedPackage ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Monitor className="h-4 w-4" />
              Allocate Credits to Your Devices
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Package Information */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{selectedPackage.package_name}</div>
                  <div className="text-sm text-muted-foreground">
                    {selectedPackage.remaining_credits} credits available
                  </div>
                  {selectedPackage.purchaser_user_id && (
                    <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <User className="h-3 w-3" />
                      Owner: {selectedPackage.purchaser_user_id}
                    </div>
                  )}
                </div>
                <Badge className="bg-purple-100 text-purple-800">
                  user owned
                </Badge>
              </div>
            </div>

            {userDevices.length === 0 ? (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  You don't have any devices assigned to you. Please contact your administrator to assign devices before allocating credits.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                {/* Device Search */}
                <div>
                  <Label htmlFor="device-search">Search Your Devices</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="device-search"
                      placeholder="Search by device ID or name..."
                      value={deviceSearchTerm}
                      onChange={(e) => setDeviceSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm text-muted-foreground">
                      Allocate {selectedPackage.remaining_credits} credits across your devices
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className={`font-medium ${remainingCredits === 0 ? 'text-green-600' : 'text-orange-600'}`}>
                        Remaining: {remainingCredits.toLocaleString()} credits
                      </span>
                      <span className="text-muted-foreground">
                        Total: {totalAllocated.toLocaleString()} / {selectedPackage.remaining_credits.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={redistributeCredits}
                      className="text-xs"
                    >
                      Redistribute Equally
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={addDeviceSlot}
                      className="text-xs"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Device
                    </Button>
                  </div>
                </div>

                {/* Device Allocation Rows */}
                <div className="space-y-3">
                  {allocations.map((allocation, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 rounded-lg border bg-white">
                      <div className="flex-1">
                        <Label className="text-xs text-muted-foreground">Device {index + 1}</Label>
                        <Select value={allocation.deviceId} onValueChange={(value) => handleDeviceChange(index, value)}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select a device..." />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredDevices.map((device) => (
                              <SelectItem key={device.device_id} value={device.device_id}>
                                <div className="flex items-center justify-between w-full">
                                  <div>
                                    <div className="font-medium">
                                      {device.device_name || device.device_id}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      ID: {device.device_id}
                                    </div>
                                  </div>
                                  {getStatusBadge('assigned-to-user')}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="w-32">
                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                          <Image className="h-3 w-3" />
                          Credits
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          value={allocation.allocatedCredits}
                          onChange={(e) => handleCreditsChange(index, parseInt(e.target.value) || 0)}
                          className="mt-1 text-sm"
                          disabled={allocation.isLocked}
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleLock(index)}
                          className={`text-xs ${allocation.isLocked ? 'text-orange-600 border-orange-300' : 'text-gray-600'}`}
                        >
                          {allocation.isLocked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                        </Button>
                        
                        {allocations.length > 1 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removeDeviceSlot(index)}
                            className="text-red-600 hover:text-red-700 hover:border-red-300 text-xs"
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Allocation Summary */}
                {selectedPackage && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="text-sm space-y-1">
                      <div>
                        <span className="text-muted-foreground">Total Allocated: </span>
                        <span className={totalAllocated === selectedPackage.remaining_credits ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                          {totalAllocated.toLocaleString()} / {selectedPackage.remaining_credits.toLocaleString()}
                        </span>
                      </div>
                      {remainingCredits !== 0 && (
                        <div>
                          <span className="text-muted-foreground">Remaining: </span>
                          <span className="text-orange-600 font-medium">
                            {remainingCredits.toLocaleString()} credits
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={handleAllocateCredits}
                disabled={!isValid || combinedAssignment.isPending || userDevices.length === 0}
                className="flex-1"
                style={{ backgroundColor: '#DFEF97', color: 'black' }}
              >
                {combinedAssignment.isPending ? (
                  <>Processing...</>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Allocate Credits to Devices
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAllocationForm(false);
                  setSelectedPackage(null);
                  setAllocations([]);
                }}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {userOwnedOnly.map((pkg) => (
            <Card key={pkg.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="font-medium">{pkg.package_name}</div>
                    <div className="text-sm text-muted-foreground">
                      {pkg.remaining_credits} credits available
                    </div>
                    {pkg.purchaser_user_id && (
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <User className="h-3 w-3" />
                        Owner: {pkg.purchaser_user_id}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      Created: {format(new Date(pkg.created_at), 'MMM dd, yyyy')}
                    </div>
                  </div>
                  <div className="text-right space-y-2">
                    <div className="flex gap-2">
                      <Badge className="bg-purple-100 text-purple-800">
                        user owned
                      </Badge>
                      <Badge className="bg-blue-100 text-blue-800">
                        available
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      variant="lime"
                      onClick={() => startAllocation(pkg)}
                    >
                      <Monitor className="h-3 w-3 mr-1" />
                      Allocate to Devices
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
